import { API_PATH, BASE_PATH } from './config';
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Grid,
    IconButton,
    Tooltip,
    LinearProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import BlockIcon from '@mui/icons-material/Block';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ServerMonitor from './ServerMonitor';
import UserManagement from './UserManagement';
import AdminStatistics from './AdminStatistics';
import AdminFeatureUsage from './AdminFeatureUsage';

// Dark admin theme tokens
const dk = {
    bg: '#0d1117',
    card: '#161b22',
    elevated: '#21262d',
    hover: '#2d333b',
    border: 'rgba(99, 102, 241, 0.2)',
    borderHover: 'rgba(99, 102, 241, 0.4)',
    textPrimary: '#e6edf3',
    textSecondary: '#8b949e',
    textMuted: '#6e7681',
    accent: '#6366f1',
    accentLight: '#818cf8',
};

const AdminConsole = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);

    // AI Provider state
    const [aiProvider, setAiProvider] = useState('gemini');
    const [aiModel, setAiModel] = useState('');
    const [aiModels, setAiModels] = useState([]);
    const [aiProviders, setAiProviders] = useState(['gemini', 'copilot', 'anthropic']);
    const [aiModelsLoading, setAiModelsLoading] = useState(false);
    const [aiProviderLoading, setAiProviderLoading] = useState(false);
    const [aiProviderSaving, setAiProviderSaving] = useState(false);
    const [aiProviderMsg, setAiProviderMsg] = useState(null);

    const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

    const fetchModelsForProvider = async (provider, savedModel = null) => {
        if (provider === 'gemini') { setAiModels([]); return; }
        setAiModelsLoading(true);
        try {
            const res = await fetch(`${API_PATH}/admin/ai-models.php?type=models&provider=${encodeURIComponent(provider)}`, {
                headers: authHeaders()
            });
            const json = await res.json();
            const models = json.models ?? [];
            setAiModels(models);
            setAiModel(prev => {
                const keep = savedModel ?? prev;
                return models.includes(keep) ? keep : (models[0] ?? '');
            });
        } catch (e) {
            setAiModels([]);
        } finally {
            setAiModelsLoading(false);
        }
    };

    const BASELINE_PROVIDERS = ['gemini', 'copilot', 'anthropic'];

    const fetchProviders = async () => {
        try {
            const res = await fetch(`${API_PATH}/admin/ai-models.php?type=providers`, { headers: authHeaders() });
            const json = await res.json();
            const fromApi = Array.isArray(json.providers) ? json.providers : [];
            // Merge: baseline always present, API may add extras (e.g. openai, claude-code)
            const merged = [...BASELINE_PROVIDERS];
            fromApi.forEach(p => { if (!merged.includes(p)) merged.push(p); });
            setAiProviders(merged);
        } catch (e) {
            setAiProviders(BASELINE_PROVIDERS);
        }
    };

    const fetchAiProvider = async () => {
        setAiProviderLoading(true);
        try {
            await fetchProviders();
            const res = await fetch(`${API_PATH}/admin/ai-provider.php`, { headers: authHeaders() });
            const json = await res.json();
            const prov = json.provider ?? 'gemini';
            const mod  = json.model ?? '';
            setAiProvider(prov);
            await fetchModelsForProvider(prov, mod);
        } catch (e) {
            setAiProviderMsg({ type: 'error', text: 'Failed to load AI provider setting.' });
        } finally {
            setAiProviderLoading(false);
        }
    };

    const providerLabel = (p) => {
        const labels = { gemini: 'Gemini', copilot: 'Local CoPilot', anthropic: 'Anthropic', 'claude-code': 'Claude Code', openai: 'OpenAI' };
        return labels[p] ?? p;
    };

    const providerIcon = (p) => {
        const icons = { gemini: '☁️', copilot: '🤖', anthropic: '🧠', 'claude-code': '🤖', openai: '🔵' };
        return icons[p] ?? '🔌';
    };

    const handleProviderChange = async (newProvider) => {
        if (!newProvider) return;
        setAiProvider(newProvider);
        setAiModel('');
        await fetchModelsForProvider(newProvider);
    };

    const saveAiProvider = async (newProvider, newModel) => {
        const providerToSave = newProvider ?? aiProvider;
        const modelToSave    = newModel    ?? aiModel;
        setAiProviderSaving(true);
        setAiProviderMsg(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_PATH}/admin/ai-provider.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ provider: providerToSave, model: modelToSave })
            });
            const json = await res.json();
            if (json.success) {
                setAiProvider(json.provider);
                setAiModel(json.model);
                setAiProviderMsg({ type: 'success', text: `Saved: ${providerLabel(json.provider)}${json.model ? ' / ' + json.model : ''}` });
            } else {
                setAiProviderMsg({ type: 'error', text: json.error || 'Failed to save.' });
            }
        } catch (e) {
            setAiProviderMsg({ type: 'error', text: 'Failed to save AI provider setting.' });
        } finally {
            setAiProviderSaving(false);
        }
    };

    // Initialize activeTab from URL parameter or default to 0
    const tabParam = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState(() => {
        if (tabParam === 'server-monitor') return 1;
        if (tabParam === 'user-management') return 2;
        if (tabParam === 'ai-provider') return 3;
        if (tabParam === 'statistics') return 4;
        if (tabParam === 'feature-usage') return 5;
        return 0;
    });

    useEffect(() => {
        // Update tab if URL parameter changes
        if (tabParam === 'server-monitor') {
            setActiveTab(1);
        } else if (tabParam === 'user-management') {
            setActiveTab(2);
        } else if (tabParam === 'ai-provider') {
            setActiveTab(3);
        } else if (tabParam === 'statistics') {
            setActiveTab(4);
        } else if (tabParam === 'feature-usage') {
            setActiveTab(5);
        } else {
            setActiveTab(0);
        }
    }, [tabParam]);

    useEffect(() => {
        if (activeTab === 3) fetchAiProvider();
    }, [activeTab]); // eslint-disable-line

    const fetchApiKeyStatus = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_PATH}/admin/api-keys-status.php`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.status === 403) {
                setError('Access denied. This page is only accessible to administrators.');
                setLoading(false);
                return;
            }

            if (response.status === 401) {
                setError('Please log in to access this page.');
                setLoading(false);
                return;
            }

            const result = await response.json();

            if (result.success) {
                setData(result);
                setLastRefresh(new Date());
            } else {
                setError(result.error || 'Failed to fetch API key status');
            }
        } catch (err) {
            setError('Failed to connect to server: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Do NOT auto-check on load - user must click the button

    const getStatusIcon = (status) => {
        switch (status) {
            case 'working':
                return <CheckCircleIcon sx={{ color: '#10b981' }} />;
            case 'quota_exceeded':
                return <ErrorIcon sx={{ color: '#ef4444' }} />;
            case 'rate_limited':
                return <WarningIcon sx={{ color: '#f59e0b' }} />;
            case 'blocked_referrer':
            case 'permission_denied':
            case 'invalid':
                return <BlockIcon sx={{ color: '#ef4444' }} />;
            default:
                return <ErrorIcon sx={{ color: dk.textMuted }} />;
        }
    };

    const getStatusChip = (status) => {
        const statusConfig = {
            working:          { label: 'Working',           color: '#10b981', bg: 'rgba(16,185,129,0.15)'  },
            quota_exceeded:   { label: 'Quota Exceeded',    color: '#ef4444', bg: 'rgba(239,68,68,0.15)'   },
            rate_limited:     { label: 'Rate Limited',      color: '#f59e0b', bg: 'rgba(245,158,11,0.15)'  },
            blocked_referrer: { label: 'Blocked (Referrer)',color: '#ef4444', bg: 'rgba(239,68,68,0.15)'   },
            permission_denied:{ label: 'Permission Denied', color: '#ef4444', bg: 'rgba(239,68,68,0.15)'   },
            invalid:          { label: 'Invalid Key',       color: '#ef4444', bg: 'rgba(239,68,68,0.15)'   },
            error:            { label: 'Error',             color: dk.textSecondary, bg: 'rgba(139,148,158,0.15)' },
            unknown:          { label: 'Unknown',           color: dk.textSecondary, bg: 'rgba(139,148,158,0.15)' },
        };
        const config = statusConfig[status] || statusConfig.unknown;
        return (
            <Chip
                label={config.label}
                size="small"
                sx={{
                    color: config.color,
                    bgcolor: config.bg,
                    border: `1px solid ${config.color}40`,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                }}
            />
        );
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto', bgcolor: dk.bg, minHeight: '100vh' }}>
            {/* Header with Select Dropdown */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton
                    onClick={() => navigate('/')}
                    sx={{ color: dk.textSecondary, '&:hover': { bgcolor: dk.elevated, color: dk.textPrimary } }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <FormControl sx={{ minWidth: 300 }}>
                    <InputLabel
                        id="admin-console-select-label"
                        sx={{ color: dk.textSecondary, '&.Mui-focused': { color: dk.accentLight } }}
                    >
                        🔧 Admin Console
                    </InputLabel>
                    <Select
                        labelId="admin-console-select-label"
                        id="admin-console-select"
                        value={activeTab}
                        onChange={(e) => setActiveTab(e.target.value)}
                        label="🔧 Admin Console"
                        sx={{
                            color: dk.textPrimary,
                            bgcolor: dk.card,
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: dk.border },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: dk.borderHover },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: dk.accent },
                            '& .MuiSvgIcon-root': { color: dk.textSecondary },
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: { bgcolor: dk.elevated, border: `1px solid ${dk.border}` }
                            }
                        }}
                    >
                        <MenuItem value={0} sx={{ color: dk.textPrimary, '&:hover': { bgcolor: dk.hover }, '&.Mui-selected': { bgcolor: `${dk.accent}25`, color: dk.accentLight } }}>🔑 API Keys Status</MenuItem>
                        <MenuItem value={1} sx={{ color: dk.textPrimary, '&:hover': { bgcolor: dk.hover }, '&.Mui-selected': { bgcolor: `${dk.accent}25`, color: dk.accentLight } }}>📊 Server Monitor</MenuItem>
                        <MenuItem value={2} sx={{ color: dk.textPrimary, '&:hover': { bgcolor: dk.hover }, '&.Mui-selected': { bgcolor: `${dk.accent}25`, color: dk.accentLight } }}>👥 User Management</MenuItem>
                        <MenuItem value={3} sx={{ color: dk.textPrimary, '&:hover': { bgcolor: dk.hover }, '&.Mui-selected': { bgcolor: `${dk.accent}25`, color: dk.accentLight } }}>🤖 AI Provider</MenuItem>
                        <MenuItem value={4} sx={{ color: dk.textPrimary, '&:hover': { bgcolor: dk.hover }, '&.Mui-selected': { bgcolor: `${dk.accent}25`, color: dk.accentLight } }}>📈 Statistics</MenuItem>
                        <MenuItem value={5} sx={{ color: dk.textPrimary, '&:hover': { bgcolor: dk.hover }, '&.Mui-selected': { bgcolor: `${dk.accent}25`, color: dk.accentLight } }}>📊 Feature Usage</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Tab 0: API Keys Status */}
            {activeTab === 0 && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" component="h2" sx={{ flexGrow: 1, color: dk.textPrimary, fontWeight: 700 }}>
                            Gemini API Keys Status Check
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <RefreshIcon />}
                            onClick={fetchApiKeyStatus}
                            disabled={loading}
                            sx={{
                                bgcolor: dk.accent,
                                '&:hover': { bgcolor: '#4f46e5' },
                                '&.Mui-disabled': { bgcolor: dk.elevated, color: dk.textMuted },
                            }}
                        >
                            Refresh
                        </Button>
                    </Box>

                    {/* Error Alert */}
                    {error && (
                        <Alert
                            severity="error"
                            sx={{ mb: 3, bgcolor: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', '& .MuiAlert-icon': { color: '#f87171' } }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Loading */}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                            <CircularProgress sx={{ color: dk.accent }} />
                        </Box>
                    )}

                    {/* Initial State - No data yet */}
                    {!data && !loading && !error && (
                        <Paper sx={{ p: 5, textAlign: 'center', bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                            <Typography variant="h5" gutterBottom sx={{ color: dk.textPrimary, fontWeight: 700 }}>
                                Gemini API Keys Status Check
                            </Typography>
                            <Typography variant="body1" sx={{ color: dk.textSecondary, mb: 3 }}>
                                Click the button below to check the status of all configured API keys.
                                <br />
                                This will test each key and report quota, rate limits, and permissions.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<RefreshIcon />}
                                onClick={fetchApiKeyStatus}
                                sx={{ px: 4, py: 1.5, bgcolor: dk.accent, '&:hover': { bgcolor: '#4f46e5' } }}
                            >
                                Check API Keys Status
                            </Button>
                        </Paper>
                    )}

                    {/* Content */}
                    {data && (
                        <>
                            {/* Summary Cards */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                            <Typography variant="h4" sx={{ color: dk.accentLight, fontWeight: 700 }}>{data.summary.total}</Typography>
                                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Total Keys</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card sx={{ bgcolor: dk.card, border: '1px solid rgba(16,185,129,0.35)', borderRadius: 2, boxShadow: 'none' }}>
                                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                            <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 700 }}>{data.summary.working}</Typography>
                                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Working</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card sx={{ bgcolor: dk.card, border: '1px solid rgba(239,68,68,0.35)', borderRadius: 2, boxShadow: 'none' }}>
                                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                            <Typography variant="h4" sx={{ color: '#ef4444', fontWeight: 700 }}>{data.summary.quota_exceeded}</Typography>
                                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Quota Exceeded</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card sx={{ bgcolor: dk.card, border: '1px solid rgba(245,158,11,0.35)', borderRadius: 2, boxShadow: 'none' }}>
                                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                            <Typography variant="h4" sx={{ color: '#f59e0b', fontWeight: 700 }}>{data.summary.rate_limited}</Typography>
                                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Rate Limited</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                            <Typography variant="h4" sx={{ color: dk.textSecondary, fontWeight: 700 }}>{data.summary.blocked}</Typography>
                                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Blocked</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Card sx={{ bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                            <Typography variant="h4" sx={{ color: dk.textSecondary, fontWeight: 700 }}>{data.summary.errors}</Typography>
                                            <Typography variant="body2" sx={{ color: dk.textSecondary }}>Errors</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Last Refresh */}
                            <Typography variant="body2" sx={{ color: dk.textMuted, mb: 2 }}>
                                Last checked: {lastRefresh?.toLocaleString()} (Server time: {data.timestamp})
                            </Typography>

                            {/* API Keys Table */}
                            <Paper sx={{ mb: 3, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, overflow: 'hidden', boxShadow: 'none' }}>
                                <Typography variant="h6" sx={{ p: 2, borderBottom: `1px solid ${dk.border}`, color: dk.textPrimary, fontWeight: 700 }}>
                                    Gemini API Keys Status
                                </Typography>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: dk.elevated }}>
                                                {['#', 'API Key', 'Status', 'Message', 'RPM Usage'].map((h) => (
                                                    <TableCell key={h} sx={{ color: dk.textSecondary, borderBottom: `1px solid ${dk.border}`, fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.keys.map((key) => (
                                                <TableRow
                                                    key={key.index}
                                                    sx={{
                                                        bgcolor: key.status === 'working'        ? 'rgba(16,185,129,0.05)'  :
                                                                 key.status === 'quota_exceeded' ? 'rgba(239,68,68,0.05)'   :
                                                                 key.status === 'blocked_referrer'? 'rgba(139,148,158,0.04)' :
                                                                 'transparent',
                                                        '&:hover': { bgcolor: dk.hover },
                                                        '& td': { borderBottom: `1px solid ${dk.border}` },
                                                    }}
                                                >
                                                    <TableCell sx={{ color: dk.textSecondary }}>{key.index}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: dk.accentLight }}>
                                                            {key.masked_key}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {getStatusIcon(key.status)}
                                                            {getStatusChip(key.status)}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ color: dk.textPrimary }}>
                                                            {key.status_message}
                                                        </Typography>
                                                        {key.error_details && (
                                                            <Tooltip title={key.error_details}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        display: 'block',
                                                                        maxWidth: 300,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        cursor: 'help',
                                                                        color: dk.textMuted,
                                                                    }}
                                                                >
                                                                    {key.error_details}
                                                                </Typography>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ width: 80 }}>
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={(key.local_rate_limit.requests_last_minute / key.local_rate_limit.max_rpm) * 100}
                                                                    sx={{
                                                                        height: 6,
                                                                        borderRadius: 3,
                                                                        bgcolor: dk.elevated,
                                                                        '& .MuiLinearProgress-bar': {
                                                                            bgcolor: key.local_rate_limit.is_limited ? '#ef4444' : dk.accent,
                                                                            borderRadius: 3,
                                                                        },
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Typography variant="caption" sx={{ color: dk.textSecondary }}>
                                                                {key.local_rate_limit.requests_last_minute}/{key.local_rate_limit.max_rpm}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>

                            {/* Help Section */}
                            <Paper sx={{ p: 3, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none' }}>
                                <Typography variant="h6" gutterBottom sx={{ color: dk.textPrimary, fontWeight: 700 }}>
                                    Troubleshooting Guide
                                </Typography>
                                {[
                                    { title: 'Quota Exceeded', body: 'Daily quota is used up. Quotas reset at midnight Pacific Time. Consider creating API keys from different Google Cloud projects to increase total quota.' },
                                    { title: 'Rate Limited', body: 'Too many requests per minute (15 RPM limit). The system will automatically rotate to other keys.' },
                                    { title: 'Blocked (Referrer)', body: 'The API key has HTTP referrer restrictions. Go to Google Cloud Console → APIs & Credentials → Edit the key → Set "Application restrictions" to "None" or "IP addresses" with your server IP.' },
                                    { title: 'Note', body: 'Keys from the same Google Cloud project share the same daily quota. For maximum availability, use keys from different projects.' },
                                ].map(({ title, body }) => (
                                    <Typography key={title} variant="body2" paragraph sx={{ color: dk.textSecondary, mb: 1.5 }}>
                                        <Box component="span" sx={{ color: dk.accentLight, fontWeight: 600 }}>{title}:</Box>{' '}{body}
                                    </Typography>
                                ))}
                            </Paper>
                        </>
                    )}
                </>
            )}

            {/* Tab 1: Server Monitor */}
            {activeTab === 1 && (
                <ServerMonitor />
            )}

            {/* Tab 2: User Management */}
            {activeTab === 2 && (
                <UserManagement />
            )}

            {/* Tab 3: AI Provider */}
            {activeTab === 3 && (
                <Paper sx={{ p: 4, bgcolor: dk.card, border: `1px solid ${dk.border}`, borderRadius: 2, boxShadow: 'none', maxWidth: 520 }}>
                    <Typography variant="h6" sx={{ color: dk.textPrimary, fontWeight: 700, mb: 1 }}>🤖 AI Provider</Typography>
                    <Typography variant="body2" sx={{ color: dk.textSecondary, mb: 3 }}>
                        Select which AI engine handles all chat requests for every user.
                        This is a server-level setting — individual users cannot override it.
                    </Typography>

                    {aiProviderLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: dk.accent }} />
                        </Box>
                    ) : (
                        <>
                            {/* Provider selector — built from live /v1/providers list */}
                            <ToggleButtonGroup
                                value={aiProvider}
                                exclusive
                                onChange={(_, v) => { if (v) handleProviderChange(v); }}
                                disabled={aiProviderSaving}
                                sx={{
                                    mb: 3,
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                    '& .MuiToggleButton-root': {
                                        color: dk.textSecondary,
                                        borderColor: dk.border,
                                        px: 3,
                                        py: 1.5,
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        '&.Mui-selected': {
                                            color: dk.accentLight,
                                            bgcolor: `${dk.accent}25`,
                                            borderColor: dk.accent,
                                            '&:hover': { bgcolor: `${dk.accent}35` },
                                        },
                                        '&:hover': { bgcolor: dk.hover },
                                    },
                                }}
                            >
                                {aiProviders.map(p => (
                                    <ToggleButton key={p} value={p}>{providerIcon(p)} {providerLabel(p)}</ToggleButton>
                                ))}
                            </ToggleButtonGroup>

                            {/* Model selector — shown for providers that have selectable models */}
                            {aiProvider !== 'gemini' && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ color: dk.textSecondary }}>Model</Typography>
                                        {aiModelsLoading
                                            ? <CircularProgress size={12} sx={{ color: dk.accent }} />
                                            : <Tooltip title="Refresh model list">
                                                <IconButton size="small" onClick={() => fetchModelsForProvider(aiProvider, aiModel)} sx={{ color: dk.textMuted, p: 0.3 }}>
                                                    <RefreshIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                              </Tooltip>
                                        }
                                    </Box>
                                    {aiModels.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                                            {aiModels.map(m => (
                                                <Box
                                                    key={m}
                                                    onClick={() => { if (!aiProviderSaving) setAiModel(m); }}
                                                    sx={{
                                                        px: 2, py: 0.75,
                                                        borderRadius: 1,
                                                        border: `1px solid ${aiModel === m ? dk.accent : dk.border}`,
                                                        bgcolor: aiModel === m ? `${dk.accent}25` : dk.elevated,
                                                        color: aiModel === m ? dk.accentLight : dk.textSecondary,
                                                        fontWeight: 600,
                                                        fontSize: '0.82rem',
                                                        fontFamily: 'monospace',
                                                        cursor: aiProviderSaving ? 'not-allowed' : 'pointer',
                                                        '&:hover': { borderColor: dk.borderHover },
                                                    }}
                                                >
                                                    {m}
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        !aiModelsLoading && (
                                            <Typography variant="body2" sx={{ color: dk.textMuted, mb: 3 }}>
                                                No models available — check API key configuration.
                                            </Typography>
                                        )
                                    )}
                                </>
                            )}

                            {/* Save button */}
                            <Button
                                variant="contained"
                                disabled={aiProviderSaving || (aiProvider !== 'gemini' && !aiModel)}
                                onClick={() => saveAiProvider(aiProvider, aiModel)}
                                sx={{ bgcolor: dk.accent, '&:hover': { bgcolor: '#4f46e5' }, '&.Mui-disabled': { bgcolor: dk.elevated, color: dk.textMuted }, mb: 2 }}
                            >
                                {aiProviderSaving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
                            </Button>

                            {aiProviderMsg && (
                                <Alert
                                    severity={aiProviderMsg.type}
                                    sx={{
                                        mb: 2,
                                        bgcolor: aiProviderMsg.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                                        color: aiProviderMsg.type === 'success' ? '#10b981' : '#f87171',
                                        border: `1px solid ${aiProviderMsg.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                                        '& .MuiAlert-icon': { color: aiProviderMsg.type === 'success' ? '#10b981' : '#f87171' }
                                    }}
                                >
                                    {aiProviderMsg.text}
                                </Alert>
                            )}

                            <Box sx={{ mt: 1, p: 2, bgcolor: dk.elevated, borderRadius: 1.5, border: `1px solid ${dk.border}` }}>
                                <Typography variant="caption" sx={{ color: dk.textMuted, display: 'block', mb: 0.5 }}>Current setting</Typography>
                                <Typography variant="body1" sx={{ color: dk.textPrimary, fontWeight: 600 }}>
                                    {`${providerIcon(aiProvider)} ${providerLabel(aiProvider)}${aiModel ? ' — ' + aiModel : ''}`}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Paper>
            )}

            {/* Tab 4: Statistics */}
            {activeTab === 4 && (
                <AdminStatistics />
            )}

            {/* Tab 5: Feature Usage */}
            {activeTab === 5 && (
                <AdminFeatureUsage />
            )}
        </Box>
    );
};

export default AdminConsole;
